// Third party
import React, { useEffect } from 'react';
import Fuse from 'fuse.js';
import { some } from 'lodash';

// Proposal
import * as ProposalNS from '../../../Proposal';

// Utils
import User from '@/types/user';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import { createOverrideTooltipPosition } from './utils';

// Components
import AddContentButton from './AddContentButton';
import NoSearchResults from './NoSearchResults';
import SearchHeader from './SearchHeader';
import ProposalDivision from '../proposal-content-selection/ProposalDivision';
import { formatMessage } from '@/utils/i18n/i18n';

type Props = {
  user: User;
  className?: string;
  proposal: ProposalNS.Proposal;
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions;
  onToggleSection: (section: string, selected: boolean) => void;
  onToggleDivision: (division: string, selected: boolean) => void;
  onToggleHiddenLayout: (layout: string, selected: boolean) => void;
  hiddenLayouts: string[];
  selectedSections: string[];
  selectedDivisions: string[];
  pageIndexes?: ProposalNS.ProposalSectionPageMapping;
  pageRefs?: Array<React.RefObject<HTMLDivElement>>;
  locale: Locale;
  onJumpToPage: (section: string) => void;
  itemListerElement?: ProposalNS.ProposalSidebarItemLister;
  showLoaders: boolean;
};

const AdditionalContentCollapsible = (props: Props) => {
  const { user, proposal, proposalTemplateOptions, showLoaders, onToggleDivision, onToggleSection, onToggleHiddenLayout } = props;
  const { sections: selectedSections, hiddenLayouts } = proposalTemplateOptions;
  const { divisions: selectedDivisions } = proposalTemplateOptions;

  const [open, setOpen] = React.useState(false);
  const [selectedSectionsLocalState, setSelectedSectionsLocalState] = React.useState(new Map<string, boolean>());
  const [inputValue, setInputValue] = React.useState('');
  const [filterQuery, setFilterQuery] = React.useState('');
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  const overrideTooltipPosition = React.useMemo(() => createOverrideTooltipPosition(sidebarRef), []);

  useEffect(() => {
    if (open && sidebarRef.current) {
      sidebarRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    selectedSectionsLocalState.keys().forEach(key => {
      const sectionHasBeenLoadedInPreview = selectedSectionsLocalState.get(key) === true && selectedSections.includes(key);
      const sectionHasBeenRemovedOffPreview = selectedSectionsLocalState.get(key) === false && !selectedSections.includes(key);
      const mustResetLoadingState = sectionHasBeenLoadedInPreview || sectionHasBeenRemovedOffPreview;

      if (mustResetLoadingState) {
        selectedSectionsLocalState.delete(key);
      }
    });
    setSelectedSectionsLocalState(new Map(selectedSectionsLocalState));

    // Avoid an infinite loop of updating localShowSectionsCache
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSections]);

  function handleFilterChange(value: string): void {
    setInputValue(value);
    setFilterQuery(value);
  }

  const filteredLabelList: LocalizedString[] = proposal.template.root.reduce((acc, division) => {
    if (division.label != null && some(Object.values(division.label))) {
      acc.push(division.label);
    }
    division.sections.forEach(section => {
      if (section.label != null && some(Object.values(section.label))) {
        acc.push(section.label);
      }
    });
    return acc;
  }, [] as Array<LocalizedString>);

  const filterOptions = {
    findAllMatches: true,
    threshold: 0.3, // lower is more strict
    keys: [props.locale],
  };

  const fuse = new Fuse(filteredLabelList, filterOptions);

  const fuseResults = fuse.search(filterQuery);
  const results = fuseResults.reduce((acc, result) => {
    acc.push(result.item);
    return acc;
  }, [] as Array<LocalizedString>);

  return (
    <>
      <AddContentButton open={open} setOpen={setOpen} />
      {open && (
        <>
          <button className="backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="proposal-toolbox-add-content opened" ref={sidebarRef} aria-label={formatMessage('Proposal.AddContent')}>
            <div className="container-something">
              <SearchHeader
                inputValue={inputValue}
                filterQuery={filterQuery}
                handleFilterChange={handleFilterChange}
                setFilterQuery={setFilterQuery}
                setInputValue={setInputValue}
              />
              <div className="divider" />
              <div className="advanced-options">
                {props.proposal.template.root.slice(0).map(division => (
                  <ProposalDivision
                    key={division.key}
                    division={division}
                    proposal={proposal}
                    user={user}
                    selectedSections={selectedSections}
                    selectedDivisions={selectedDivisions}
                    filterQuery={filterQuery}
                    results={results}
                    overrideTooltipPosition={overrideTooltipPosition}
                    onToggleDivision={onToggleDivision}
                    onToggleSection={onToggleSection}
                    onToggleHiddenLayout={onToggleHiddenLayout}
                    hiddenLayouts={hiddenLayouts}
                    selectedSectionsLocalState={selectedSectionsLocalState}
                    setSelectedSectionsLocalState={setSelectedSectionsLocalState}
                    showLoaders={showLoaders}
                  />
                ))}
                {results.length === 0 && filterQuery.length > 2 && <NoSearchResults />}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AdditionalContentCollapsible;
